import { Link } from '@tanstack/react-router';
import { ArrowLeft, FileText, CheckCircle, XCircle, AlertTriangle, RefreshCcw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardHeader, CardTitle, CardContent, Badge } from '../../shared/ui';

/**
 * Terms of Service Page
 * Public legal page accessible from landing footer and register form
 */
export function TermsPage() {
    const { t } = useTranslation();

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <header className="sticky top-0 z-50 border-b border-border bg-background px-8 py-4">
                <div className="mx-auto max-w-4xl flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Link to="/">
                            <Button variant="ghost" size="sm" corners="asymmetric" icon={<ArrowLeft className="h-4 w-4" />}>
                                {t('common.back')}
                            </Button>
                        </Link>
                    </div>
                    <div className="flex items-center gap-3">
                        <img src="/favicon-32x32.png" alt="DevLint" className="h-8 w-8" />
                        <span className="font-medium text-text-primary font-mono">DevLint</span>
                    </div>
                </div>
            </header>

            <main className="mx-auto max-w-4xl px-8 py-12">
                {/* Page Title */}
                <div className="mb-12 text-center">
                    <div className="inline-flex items-center justify-center h-16 w-16 rounded-asymmetric-lg bg-surface-hover border border-border mb-6">
                        <FileText className="h-8 w-8 text-primary" />
                    </div>
                    <h1 className="text-4xl font-bold text-text-primary font-mono tracking-tight">
                        {t('terms.title')}
                    </h1>
                    <p className="mt-4 text-text-muted font-mono">
                        {t('terms.lastUpdated')}
                    </p>
                </div>

                {/* Introduction */}
                <Card padding="lg" corners="asymmetric" className="mb-8">
                    <CardContent>
                        <p className="text-text-secondary leading-relaxed" dangerouslySetInnerHTML={{ __html: t('terms.intro') }} />
                    </CardContent>
                </Card>

                <div className="space-y-6">
                    {/* 1. Aceptación */}
                    <Card padding="lg" corners="asymmetric">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-3">
                                <span className="inline-flex h-10 w-10 items-center justify-center rounded-asymmetric-md bg-surface-hover border border-border text-primary font-mono font-bold">
                                    1
                                </span>
                                {t('terms.sections.acceptance.title')}
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <p className="text-text-secondary leading-relaxed">
                                {t('terms.sections.acceptance.content')}
                            </p>
                        </CardContent>
                    </Card>

                    {/* 2. Descripción del Servicio */}
                    <Card padding="lg" corners="asymmetric-inverse">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-3">
                                <span className="inline-flex h-10 w-10 items-center justify-center rounded-asymmetric-md bg-surface-hover border border-border text-primary font-mono font-bold">
                                    2
                                </span>
                                {t('terms.sections.serviceDescription.title')}
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <p className="text-text-secondary leading-relaxed mb-4">
                                {t('terms.sections.serviceDescription.intro')}
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {(['sast', 'ml', 'debt', 'reports', 'github', 'dashboard'] as const).map((key) => (
                                    <div key={key} className="flex items-center gap-2 text-text-secondary text-sm">
                                        <CheckCircle className="h-4 w-4 text-success flex-shrink-0" />
                                        <span>{t(`terms.sections.serviceDescription.features.${key}`)}</span>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>

                    {/* 3. Planes y Pagos */}
                    <Card padding="lg" corners="asymmetric">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-3">
                                <span className="inline-flex h-10 w-10 items-center justify-center rounded-asymmetric-md bg-surface-hover border border-border text-primary font-mono font-bold">
                                    3
                                </span>
                                {t('terms.sections.plans.title')}
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="rounded-asymmetric-md border border-border bg-surface p-4">
                                        <div className="flex items-center gap-2 mb-2">
                                            <Badge severity="low">Free</Badge>
                                        </div>
                                        <p className="text-sm text-text-muted">{t('terms.sections.plans.free')}</p>
                                    </div>
                                    <div className="rounded-asymmetric-md border border-primary/50 bg-surface p-4">
                                        <div className="flex items-center gap-2 mb-2">
                                            <Badge severity="medium">Pro</Badge>
                                        </div>
                                        <p className="text-sm text-text-muted">{t('terms.sections.plans.pro')}</p>
                                    </div>
                                    <div className="rounded-asymmetric-md border border-border bg-surface p-4">
                                        <div className="flex items-center gap-2 mb-2">
                                            <Badge severity="high">Enterprise</Badge>
                                        </div>
                                        <p className="text-sm text-text-muted">{t('terms.sections.plans.enterprise')}</p>
                                    </div>
                                </div>
                                <p className="text-sm text-text-muted">{t('terms.sections.plans.billing')}</p>
                            </div>
                        </CardContent>
                    </Card>

                    {/* 4. Uso Permitido */}
                    <Card padding="lg" corners="asymmetric-inverse">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-3">
                                <span className="inline-flex h-10 w-10 items-center justify-center rounded-asymmetric-md bg-surface-hover border border-border text-primary font-mono font-bold">
                                    4
                                </span>
                                {t('terms.sections.acceptableUse.title')}
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <h4 className="flex items-center gap-2 text-sm font-medium text-success font-mono mb-3">
                                        <CheckCircle className="h-4 w-4" />
                                        {t('terms.sections.acceptableUse.allowed')}
                                    </h4>
                                    <ul className="space-y-2">
                                        {(['own', 'org', 'export', 'cicd'] as const).map((key) => (
                                            <li key={key} className="flex items-start gap-2 text-sm text-text-secondary">
                                                <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-success flex-shrink-0" />
                                                {t(`terms.sections.acceptableUse.allowedItems.${key}`)}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                <div>
                                    <h4 className="flex items-center gap-2 text-sm font-medium text-error font-mono mb-3">
                                        <XCircle className="h-4 w-4" />
                                        {t('terms.sections.acceptableUse.prohibited')}
                                    </h4>
                                    <ul className="space-y-2">
                                        {(['reverseEng', 'sharing', 'illegal', 'overload'] as const).map((key) => (
                                            <li key={key} className="flex items-start gap-2 text-sm text-text-secondary">
                                                <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-error flex-shrink-0" />
                                                {t(`terms.sections.acceptableUse.prohibitedItems.${key}`)}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* 5. Propiedad Intelectual */}
                    <Card padding="lg" corners="asymmetric">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-3">
                                <span className="inline-flex h-10 w-10 items-center justify-center rounded-asymmetric-md bg-surface-hover border border-border text-primary font-mono font-bold">
                                    5
                                </span>
                                {t('terms.sections.ip.title')}
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-3 text-text-secondary leading-relaxed">
                                <p dangerouslySetInnerHTML={{ __html: t('terms.sections.ip.yourCode') }} />
                                <p dangerouslySetInnerHTML={{ __html: t('terms.sections.ip.ourPlatform') }} />
                            </div>
                        </CardContent>
                    </Card>

                    {/* 6. Limitación de Responsabilidad */}
                    <Card padding="lg" corners="asymmetric-inverse">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-3">
                                <span className="inline-flex h-10 w-10 items-center justify-center rounded-asymmetric-md bg-warning/20 border border-warning/30 text-warning font-mono font-bold">
                                    <AlertTriangle className="h-5 w-5" />
                                </span>
                                {t('terms.sections.liability.title')}
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="rounded-asymmetric-md border border-warning/30 bg-warning/5 p-4">
                                <p className="text-sm text-text-secondary leading-relaxed" dangerouslySetInnerHTML={{ __html: t('terms.sections.liability.content') }} />
                            </div>
                        </CardContent>
                    </Card>

                    {/* 7. Modificaciones */}
                    <Card padding="lg" corners="asymmetric">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-3">
                                <span className="inline-flex h-10 w-10 items-center justify-center rounded-asymmetric-md bg-surface-hover border border-border text-primary font-mono font-bold">
                                    <RefreshCcw className="h-5 w-5" />
                                </span>
                                {t('terms.sections.modifications.title')}
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <p className="text-text-secondary leading-relaxed">
                                {t('terms.sections.modifications.content')}
                            </p>
                        </CardContent>
                    </Card>
                </div>
            </main>

            {/* Footer */}
            <footer className="border-t border-border py-8 mt-12">
                <div className="mx-auto max-w-4xl px-8">
                    <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
                        <p className="text-sm text-text-muted font-mono">{t('landing.footer.copyright')}</p>
                        <div className="flex gap-6 text-sm text-text-muted font-mono">
                            <Link to="/privacy" className="hover:text-text-primary transition-colors">{t('landing.footer.privacy')}</Link>
                            <Link to="/contact" className="hover:text-text-primary transition-colors">{t('landing.footer.contact')}</Link>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}
